import { useEffect, useCallback, useState } from 'react';
import { GameState } from '../types/game';
import { gameDB } from '../services/db';
import { gameService } from '../services/gameService';
import { GAME_CONFIG } from '../config/game';
import toast from 'react-hot-toast';
import { soundService } from '../services/soundService';

export const DEFAULT_GAME_STATE: GameState = {
  clicks: 0,
  balance: 0,
  multiplierCount: 1,
  autoClickerCount: 0,
  multipliers: {},
  autoClickers: [],
  miningClicks: 0,
  pendingWei: 0,
  pendingClicks: 0,
  totalSpentMultipliers: 0,
  totalSpentAutoClickers: 0,
  autoClickerActive: false,
  lastCritical: false,
  lastEarned: 0,
  lastSaved: Date.now(),
  level: 1,
  experience: 0,
  activeBoosts: [],
  upgradeLevels: {},
  coinsPerClick: 1,
  prestige: 0
};

export function useGameState(walletAddress: string | null) {
  const [gameState, setGameState] = useState<GameState>(DEFAULT_GAME_STATE);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Save state
  const saveState = useCallback(async (newState: GameState) => {
    if (!walletAddress) return;
    try {
      await gameDB.saveGameState(walletAddress, {
        ...newState,
        lastSaved: Date.now()
      });
    } catch (error) {
      console.error('Error saving game state:', error);
      setError(error instanceof Error ? error : new Error('Failed to save game state'));
      toast.error('Error saving game state');
    }
  }, [walletAddress]);

  // Update state
  const updateGameState = useCallback((updates: Partial<GameState>) => {
    setGameState(prev => {
      try {
        const newState = {
          ...prev,
          ...updates,
          lastSaved: Date.now()
        };

        // Check if player levels up
        if ('experience' in updates) {
          const expForNextLevel = Math.floor(GAME_CONFIG.levelUpThreshold * Math.pow(1.5, prev.level - 1));
          if (newState.experience >= expForNextLevel && prev.level < GAME_CONFIG.maxLevel) {
            newState.level = prev.level + 1;
            newState.experience = newState.experience - expForNextLevel;
            try {
              soundService.playLevelUp();
            } catch (error) {
              console.warn('Error playing level up sound:', error);
            }
            toast.success(`Level ${newState.level} reached!`);
          }
        }

        saveState(newState);
        return newState;
      } catch (error) {
        console.error('Error updating game state:', error);
        setError(error instanceof Error ? error : new Error('Failed to update game state'));
        toast.error('Error updating game state');
        return prev;
      }
    });
  }, [saveState]);

  // Reset state
  const resetGameState = useCallback(async () => {
    if (!walletAddress) return;
    try {
      setGameState(DEFAULT_GAME_STATE);
      await gameDB.clearData(walletAddress);
      gameService.clearGameData(walletAddress);
      toast.success('All data has been reset');
    } catch (error) {
      console.error('Error resetting game state:', error);
      setError(error instanceof Error ? error : new Error('Failed to reset game state'));
      toast.error('Error resetting game state');
    }
  }, [walletAddress]);

  // Load initial state
  useEffect(() => {
    async function loadState() {
      if (!walletAddress) {
        setGameState(DEFAULT_GAME_STATE);
        setIsLoading(false);
        return;
      }

      try {
        const savedState = await gameDB.loadGameState(walletAddress);
        if (savedState) {
          setGameState({
            ...DEFAULT_GAME_STATE,
            ...savedState,
            activeBoosts: savedState.activeBoosts || [],
            upgradeLevels: savedState.upgradeLevels || {},
            multipliers: savedState.multipliers || {},
            autoClickers: savedState.autoClickers || []
          });
        } else {
          setGameState(DEFAULT_GAME_STATE);
        }
        setError(null);
      } catch (error) {
        console.error('Error loading game state:', error);
        setError(error instanceof Error ? error : new Error('Failed to load game state'));
        toast.error('Error loading game state');
        setGameState(DEFAULT_GAME_STATE);
      } finally {
        setIsLoading(false);
      }
    }

    loadState();
  }, [walletAddress]);

  // Save periodically
  useEffect(() => {
    if (!walletAddress) return;
    const saveInterval = setInterval(() => {
      saveState(gameState);
    }, 10000);
    return () => clearInterval(saveInterval);
  }, [gameState, walletAddress, saveState]);

  // Clean up expired boosts
  useEffect(() => {
    const cleanupInterval = setInterval(() => {
      const now = Date.now();
      const activeBoosts = gameState.activeBoosts.filter(boost => boost.endsAt > now);
      
      if (activeBoosts.length !== gameState.activeBoosts.length) {
        updateGameState({ activeBoosts });
      }
    }, 1000);

    return () => clearInterval(cleanupInterval);
  }, [gameState.activeBoosts, updateGameState]);

  return {
    gameState,
    updateGameState,
    resetGameState,
    isLoading,
    error
  };
}